import { defineStore } from 'pinia'
import type { NotificationEntity } from '~/generated-api-client'

export type NotificationFilter = 'all' | 'unread'

const PAGE_SIZE = 15

export const useNotificationStore = defineStore('notification', () => {
  const items = ref<NotificationEntity[]>([])
  const filter = ref<NotificationFilter>('all')
  const page = ref(1)
  const totalPages = ref(1)
  const total = ref(0)
  const unreadCount = ref(0)

  const loading = ref(false)
  const loadingMore = ref(false)
  const markingAll = ref(false)
  const error = ref<string | null>(null)
  const loaded = ref(false)

  const hasMore = computed(() => page.value < totalPages.value)
  const hasUnread = computed(() => unreadCount.value > 0)

  function createdAtMs(notification: NotificationEntity): number {
    const value = new Date(notification.created_at).getTime()
    return Number.isNaN(value) ? 0 : value
  }

  async function fetchPage(nextPage = 1): Promise<void> {
    const { $mwApi } = useNuxtApp()
    const requestedFilter = filter.value

    if (nextPage > 1) loadingMore.value = true
    else loading.value = true
    error.value = null

    try {
      const { data } = await $mwApi.getNotifications(
        nextPage,
        PAGE_SIZE,
        requestedFilter === 'unread' ? true : undefined,
      )

      // Ignore a response that lost the race against a filter switch.
      if (filter.value !== requestedFilter) return

      if (nextPage <= 1) {
        items.value = data.data
      } else {
        const known = new Set(items.value.map((notification) => notification.id))
        items.value.push(...data.data.filter((notification) => !known.has(notification.id)))
      }

      page.value = data.page
      totalPages.value = Math.max(data.totalPages, 1)
      total.value = data.total
      unreadCount.value = data.unreadCount
      loaded.value = true
    } catch (err: any) {
      error.value = err?.response?.data?.message ?? err?.message ?? 'error'
      throw err
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  /** Kept for the layout's SSR prefetch, which only needs the badge count. */
  async function fetchNotifications(): Promise<void> {
    await fetchPage(1)
  }

  async function loadMore(): Promise<void> {
    if (!hasMore.value || loadingMore.value || loading.value) return
    await fetchPage(page.value + 1)
  }

  async function setFilter(next: NotificationFilter): Promise<void> {
    if (filter.value === next) return
    filter.value = next
    items.value = []
    page.value = 1
    totalPages.value = 1
    total.value = 0
    await fetchPage(1)
  }

  /**
   * Mark specific notifications read. The badge is set from the count the
   * server recomputes, not decremented locally — two tabs, or a push landing
   * mid-request, made the local arithmetic drift.
   */
  async function markAsRead(ids: string[]): Promise<void> {
    const unread = ids.filter((id) => items.value.some((n) => n.id === id && !n.is_read))
    if (!unread.length) return

    const { $mwApi } = useNuxtApp()
    const { data } = await $mwApi.markAsRead({ notificationIds: unread })

    items.value = items.value.map((notification) =>
      unread.includes(notification.id) ? { ...notification, is_read: true } : notification,
    )
    unreadCount.value = data.unreadCount

    // On the unread-only tab a row that has just been read no longer belongs,
    // but yanking it out from under the pointer is disorienting — it is dropped
    // on the next fetch instead.
  }

  async function markAllAsRead(): Promise<void> {
    if (!unreadCount.value || markingAll.value) return

    const { $mwApi } = useNuxtApp()
    markingAll.value = true
    try {
      const { data } = await $mwApi.markAllAsRead()
      unreadCount.value = data.unreadCount
      items.value = items.value.map((notification) => ({ ...notification, is_read: true }))
      if (filter.value === 'unread') await fetchPage(1)
    } finally {
      markingAll.value = false
    }
  }

  /** Realtime arrival (websocket `notification` event). */
  function push(notification: NotificationEntity): void {
    if (items.value.some((existing) => existing.id === notification.id)) return

    const belongs = filter.value === 'all' || !notification.is_read
    if (belongs) {
      const at = items.value.findIndex((existing) => createdAtMs(existing) < createdAtMs(notification))
      if (at === -1) items.value.unshift(notification)
      else items.value.splice(at, 0, notification)
      total.value += 1
    }

    if (!notification.is_read) unreadCount.value += 1
  }

  function reset(): void {
    items.value = []
    filter.value = 'all'
    page.value = 1
    totalPages.value = 1
    total.value = 0
    unreadCount.value = 0
    error.value = null
    loaded.value = false
    loading.value = false
    loadingMore.value = false
  }

  return {
    items,
    filter,
    page,
    totalPages,
    total,
    unreadCount,
    hasUnread,
    hasMore,
    loading,
    loadingMore,
    markingAll,
    loaded,
    error,
    fetchPage,
    fetchNotifications,
    loadMore,
    setFilter,
    markAsRead,
    markAllAsRead,
    push,
    reset,
  }
})
